import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import comparePriceToParent from '@sportson/core-web/utils/comparePriceToParent';
import decodeHTMLEntities from '@sportson/core-web/utils/decodeHTMLEntities';
import getProductStockStatus from '@sportson/core-web/utils/getProductStockStatus';
import { above } from '@sportson/core-web/utils/mediaqueries';
import Image from '@sportson/core-web/components/Image';
import Link from '@sportson/core-web/components/Link';
import styled from 'libs/styled';
import objectKeysToCamelCase from '@grebban/utils/object/keysToCamelCase';

import { stormSrc, generateSrcSet } from '@sportson/core-web/libs/image';

import { Price } from 'components/product/Price';
import Text from 'components/Text';
import { AddToCart } from 'components/productPage/AddToCart';

const Div = styled('div')`
    display: flex;
`;

const ProductCard = styled('div')`
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    gap: 24px;
    padding: 16px;
    border-radius: 4px;
    background-color: var(--color-neutrals-100);
`;

const ContentWrapper = styled('div')`
    display: flex;
    gap: 12px;
    flex: 1;
    min-height: auto;
    /* Uncomment when client implemented highlighted product "tag"  */
    /* ${({ firstGridItem }) =>
        firstGridItem &&
        `
        min-height: 250px;
        ${above['tablet.sm']} {
            min-height: auto;
        }
    `}; */
`;

const ImageWrapper = styled('div')`
    display: flex;
    flex-direction: column;
    align-self: center;
    max-width: 30%;

    img {
        mix-blend-mode: multiply;
    }
`;

const StyledText = styled(Text)`
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 1;
    -webkit-box-orient: vertical;

    /*  Uncomment when client implemented highlighted product "tag"  */
    /* ${({ firstGridItem }) =>
        firstGridItem &&
        ` -webkit-line-clamp: 3;
        line-clamp: 3;`} */
`;

export const RelationsProduct = ({ product, i }) => {
    const firstGridItem = i === 0;
    const { t } = useTranslation();

    product = objectKeysToCamelCase(product, {
        recursive: true,
        modifyInputObject: false,
    });

    const {
        name,
        imageKey,
        manufacturer: { name: manufacturerName },
        description,
        descriptionHeader: descHeader,
        variants,
        price: priceExVat,
        priceStandard: standardPriceExVat,
        vatRate,
        id,
        url,
        uniqueName,
        priceListId,
    } = product;

    const stockStatus = getProductStockStatus(product?.on_hand);
    const productName = decodeHTMLEntities(descHeader || name);

    const isVariant = product.variants.length > 1;
    const linkTo = url;
    const inStock = !!stockStatus.stock;

    const widths = [360, 460, 560, 660, 760];

    const imageSrc = stormSrc(`${imageKey}.jpg`, { auto: 'format', q: 80, ar: '1', w: 88, h: 117, fit: 'fill' });
    const srcSet = generateSrcSet(imageSrc, widths);

    // Preparing product array for AddToCart component
    const productarray = {};
    productarray[product.id] = product;

    return (
        <div>
            <ProductCard>
                {/* Uncomment when client implemented highlighted product "tag" */}
                {/* {firstGridItem && (
                    <Text
                        typography="UI/14_100_0_450_uppercase"
                        backgroundColor="var(--background-color-default)"
                        width="fit-content"
                        padding="6px 8px 4px"
                        borderRadius="4px"
                    >
                        sportson rekommenderar
                    </Text>
                )} */}
                <ContentWrapper firstGridItem={firstGridItem}>
                    <ImageWrapper>
                        {!imageKey ? (
                            <Text typography="UI/12_100_0_350">{t('image_not_available')}</Text>
                        ) : (
                            <Image
                                alt={uniqueName}
                                title={name}
                                src={{ url: imageSrc, width: widths }}
                                srcSet={srcSet}
                                width="90px"
                                height="90px"
                                objectFit="contain"
                                loading="lazy"
                            />
                        )}
                    </ImageWrapper>
                    <Div flexDirection="column" width="100%" gap="12px">
                        <Div
                            flexDirection="column"
                            // gap={firstGridItem ? ['12px', null, null, '8px'] : ['8px', null, null, '4px']} //  Uncomment when client implemented highlighted product "tag"
                            gap={['8px', null, null, '4px']}
                            height="100%"
                            overflow="hidden"
                        >
                            <Link to={linkTo}>
                                <Text typography="UI/16_100_0_450">{manufacturerName}</Text>
                            </Link>
                            <StyledText
                                firstGridItem={firstGridItem}
                                typography="Content/14_130_0_350"
                                color="var(--color-neutrals-500)"
                            >
                                {description || productName}
                            </StyledText>
                        </Div>
                        <Div
                            gap={['12px', null, null, '8px']}
                            flexDirection={['column', null, null, 'row']}
                            justifyContent={['unset', null, null, 'space-between']}
                            alignItems={['flex-start', null, null, 'center']}
                        >
                            <Price
                                typography="UI/16_100_0_450"
                                priceExVat={product?.price || priceExVat}
                                priceListId={product?.price_list_id || priceListId}
                                showFromPrice={isVariant && !product}
                                standardPriceExVat={
                                    comparePriceToParent(product?.included_products) ||
                                    product?.price_standard ||
                                    standardPriceExVat
                                }
                                vatRate={vatRate}
                            />
                            <AddToCart
                                showCartIcon={false}
                                fullWidth={[true, null, null, false]}
                                inStock={inStock}
                                mainProduct={product}
                                productId={product.id}
                                buttonText="basket.add"
                                buttonTheme="primaryDark"
                                selectedProductVariants={productarray}
                            />
                        </Div>
                    </Div>
                </ContentWrapper>
            </ProductCard>
        </div>
    );
};

RelationsProduct.propTypes = {
    activeDropdown: PropTypes.object.isRequired,
    handleMenu: PropTypes.func.isRequired,
    i: PropTypes.number.isRequired,
    product: PropTypes.object.isRequired,
    type: PropTypes.string.isRequired,
};
